import { useMemo, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Calculator, Info } from 'lucide-react';
import {
  DEFAULT_FX_USD_TO_MXN,
  DEFAULT_UNIT_COSTS_USD,
  simulatePlanMaxCost,
} from '@/lib/planCostSimulator';

const UNIT_FIELDS = [
  { key: 'telephony_per_minute', label: 'Twilio (USD/min por pierna)' },
  { key: 'realtime_ai_per_minute', label: 'OpenAI Realtime (USD/min)' },
  { key: 'whatsapp_conversation', label: 'WhatsApp (USD/conversación)' },
  { key: 'ai_tokens_per_1k', label: 'IA chat (USD / 1k tokens)' },
  { key: 'voice_number_monthly', label: 'Renta número voz (USD/mes)' },
  { key: 'whatsapp_number_monthly', label: 'Fee número WA (USD/mes)' },
  { key: 'email_send', label: 'Email (USD/envío)' },
  { key: 'sms_send', label: 'SMS (USD/mensaje)' },
];

/**
 * Simulador de costo al tope de límites del plan.
 * props: form (plan form state)
 */
export default function PlanCostSimulator({ form }) {
  const [fx, setFx] = useState(DEFAULT_FX_USD_TO_MXN);
  const [units, setUnits] = useState({ ...DEFAULT_UNIT_COSTS_USD });
  const [linkedShare, setLinkedShare] = useState(20);
  const [linkedMode, setLinkedMode] = useState('ivr');
  const [showRates, setShowRates] = useState(false);

  const sim = useMemo(
    () =>
      simulatePlanMaxCost({
        priceMonthly: form.price_monthly,
        currency: form.currency,
        includedLimits: form.included_limits,
        includedEntitlements: form.included_entitlements,
        unitCostsUsd: units,
        fxUsdToMxn: fx,
        linkedShareOfVoiceMinutes: linkedShare,
        linkedMode,
      }),
    [form.price_monthly, form.currency, form.included_limits, form.included_entitlements, units, fx, linkedShare, linkedMode],
  );

  const ref = sim.reference;
  const t = sim.totals;
  const marginPositive = t.margin_mxn >= 0;

  return (
    <div className="border border-[#E2E8F0] rounded-xl overflow-hidden" data-testid="plan-cost-simulator">
      <div className="px-4 py-3 bg-[#F8FAFC] border-b border-[#E2E8F0] flex items-start gap-2">
        <Calculator className="w-4 h-4 text-[#64748B] mt-0.5 shrink-0" />
        <div>
          <h3 className="text-sm font-semibold text-[#0F172A]">Simulador de costos (infraestructura)</h3>
          <p className="text-[11px] text-[#94A3B8] mt-0.5">
            Estima cuánto te cuesta el plan si el tenant agota todos los límites del mes.
            Úsalo para fijar el precio de venta y el margen. Tasas editables; tipo de cambio por defecto 1 USD = 20 MXN.
          </p>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Reference per-minute */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="plan-cost-reference">
          <div className="bg-white border border-[#E2E8F0] rounded-lg p-3">
            <p className="text-[10px] uppercase tracking-wide text-[#94A3B8]">1 min voz con IA</p>
            <p className="text-lg font-bold text-[#0F172A]">${ref.ai_voice_per_minute.mxn.toFixed(2)} <span className="text-xs font-normal text-[#94A3B8]">MXN</span></p>
            <p className="text-[11px] text-[#64748B]">${ref.ai_voice_per_minute.usd.toFixed(3)} USD · Twilio + Realtime</p>
          </div>
          <div className="bg-white border border-[#E2E8F0] rounded-lg p-3">
            <p className="text-[10px] uppercase tracking-wide text-[#94A3B8]">1 min enlace IVR</p>
            <p className="text-lg font-bold text-[#0F172A]">${ref.linked_ivr_per_minute.mxn.toFixed(2)} <span className="text-xs font-normal text-[#94A3B8]">MXN</span></p>
            <p className="text-[11px] text-[#64748B]">${ref.linked_ivr_per_minute.usd.toFixed(3)} USD · solo telefonía (1 pierna)</p>
          </div>
          <div className="bg-white border border-[#E2E8F0] rounded-lg p-3">
            <p className="text-[10px] uppercase tracking-wide text-[#94A3B8]">1 min enlace bridge</p>
            <p className="text-lg font-bold text-[#0F172A]">${ref.linked_bridge_per_minute.mxn.toFixed(2)} <span className="text-xs font-normal text-[#94A3B8]">MXN</span></p>
            <p className="text-[11px] text-[#64748B]">${ref.linked_bridge_per_minute.usd.toFixed(3)} USD · 2 piernas Twilio</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <label className="text-[11px] text-[#64748B] block mb-1">Tipo de cambio (1 USD = ? MXN)</label>
            <Input type="number" step="0.01" className="h-8 w-28 text-xs" value={fx}
              onChange={(e) => setFx(parseFloat(e.target.value) || 0)} data-testid="sim-fx" />
          </div>
          <div>
            <label className="text-[11px] text-[#64748B] block mb-1">% minutos de voz que son enlace (no IA)</label>
            <Input type="number" min={0} max={100} className="h-8 w-28 text-xs" value={linkedShare}
              onChange={(e) => setLinkedShare(parseFloat(e.target.value) || 0)} data-testid="sim-linked-share" />
          </div>
          <div>
            <label className="text-[11px] text-[#64748B] block mb-1">Modo enlace</label>
            <select className="h-8 px-2 text-xs border border-[#E2E8F0] rounded-md" value={linkedMode}
              onChange={(e) => setLinkedMode(e.target.value)} data-testid="sim-linked-mode">
              <option value="ivr">IVR (1 pierna)</option>
              <option value="bridge">Bridge (2 piernas)</option>
            </select>
          </div>
          <button type="button" onClick={() => setShowRates((v) => !v)}
            className="text-xs text-[#64748B] hover:text-[#0F172A] underline">
            {showRates ? 'Ocultar' : 'Editar'} tasas unitarias USD
          </button>
        </div>

        {showRates && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-3 bg-[#F8FAFC] rounded-lg" data-testid="sim-unit-rates">
            {UNIT_FIELDS.map(({ key, label }) => (
              <div key={key}>
                <label className="text-[10px] text-[#64748B] block mb-1">{label}</label>
                <Input type="number" step="0.001" className="h-8 text-xs" value={units[key]}
                  onChange={(e) => setUnits((u) => ({ ...u, [key]: parseFloat(e.target.value) || 0 }))} />
              </div>
            ))}
          </div>
        )}

        {!sim.uses_platform_infrastructure && (
          <div className="flex gap-2 items-start text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-lg p-3">
            <Info className="w-4 h-4 shrink-0 mt-0.5" />
            <p>
              Este plan <strong>no tiene</strong> “infraestructura del administrador”.
              Los costos de voz/WhatsApp/email del canal corren por la cuenta del tenant.
              Solo se simulan costos que sí absorbe la plataforma (p. ej. tokens de IA si usas tu API key de plataforma).
              Activa <em>Habilitar infraestructura del administrador</em> en el grupo Infraestructura para modelar el peor caso de costo tuyo.
            </p>
          </div>
        )}

        {/* Breakdown */}
        {sim.lines.length === 0 ? (
          <p className="text-xs text-[#94A3B8] text-center py-3">
            Define límites (minutos de voz, conversaciones WA, tokens…) para ver el costo al tope.
          </p>
        ) : (
          <div className="space-y-1.5" data-testid="sim-cost-lines">
            {sim.lines.map((line) => (
              <div key={line.key} className="flex items-start justify-between gap-3 py-1.5 border-b border-[#F1F5F9] last:border-0">
                <div className="min-w-0">
                  <p className="text-xs font-medium text-[#0F172A]">{line.label}</p>
                  <p className="text-[11px] text-[#94A3B8]">
                    {line.quantity} {line.unit} × ${line.unit_cost_usd.toFixed(4)} USD
                    {line.detail ? ` · ${line.detail}` : ''}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-xs font-semibold text-[#0F172A]">${line.total_mxn.toFixed(2)} MXN</p>
                  <p className="text-[10px] text-[#94A3B8]">${line.total_usd.toFixed(2)} USD</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Totals vs sale */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2">
          <div className="rounded-lg border border-[#E2E8F0] p-3">
            <p className="text-[10px] text-[#94A3B8] uppercase">Costo máx. infra / mes</p>
            <p className="text-xl font-bold text-[#0F172A]" data-testid="sim-cost-mxn">${t.cost_mxn.toFixed(2)} <span className="text-sm font-normal text-[#94A3B8]">MXN</span></p>
            <p className="text-[11px] text-[#64748B]">${t.cost_usd.toFixed(2)} USD</p>
          </div>
          <div className="rounded-lg border border-[#E2E8F0] p-3">
            <p className="text-[10px] text-[#94A3B8] uppercase">Precio de venta / mes</p>
            <p className="text-xl font-bold text-[#0F172A]">${Number(form.price_monthly || 0).toFixed(2)} <span className="text-sm font-normal text-[#94A3B8]">{form.currency || 'MXN'}</span></p>
            <p className="text-[11px] text-[#64748B]">≈ ${t.sale_mxn.toFixed(2)} MXN</p>
          </div>
          <div className={`rounded-lg border p-3 ${marginPositive ? 'border-emerald-200 bg-emerald-50/50' : 'border-red-200 bg-red-50/50'}`}>
            <p className="text-[10px] text-[#94A3B8] uppercase">Margen al 100% uso</p>
            <p className={`text-xl font-bold ${marginPositive ? 'text-emerald-700' : 'text-red-600'}`} data-testid="sim-margin-mxn">
              {marginPositive ? '' : '−'}${Math.abs(t.margin_mxn).toFixed(2)} <span className="text-sm font-normal">MXN</span>
            </p>
            <p className="text-[11px] text-[#64748B]">
              {t.margin_pct == null ? 'Define precio de venta' : `${t.margin_pct}% del precio`}
            </p>
          </div>
        </div>

        <p className="text-[10px] text-[#94A3B8]">
          Ejemplo: con 1 USD = {fx} MXN, 1 min de IA ≈ ${ref.ai_voice_per_minute.mxn.toFixed(2)} MXN vs 1 min enlace IVR ≈ ${ref.linked_ivr_per_minute.mxn.toFixed(2)} MXN
          (≈ {(ref.ai_voice_per_minute.mxn / Math.max(ref.linked_ivr_per_minute.mxn, 0.001)).toFixed(1)}× más cara la de IA).
          Las tasas son estimados; ajusta con tu factura real de Twilio / OpenAI / Meta.
        </p>
      </div>
    </div>
  );
}
